import { useMemo } from "react";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { DragTypes } from "../../models/DragTypes";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
  PlannerComplexityFilter,
  PlannerSort,
} from "../../models/ProductionPlanner";
import { VehicleComplexity } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { plannerSearchMatches } from "../../utils/plannerSearch";
import LoadingPulse from "../shared/LoadingPulse";
import PlannerVehicleCard from "./PlannerVehicleCard";

interface AwaitingDismantlingPanelProps {
  vehicles: IPlannerVehicleRow[];
  isLoading: boolean;
  searchTerm: string;
  complexityFilter: PlannerComplexityFilter;
  sort: PlannerSort;
  colourBy: PlannerColourBy;
  onDropFromBay: (item: IPlannerDragItem) => void;
}

const complexityMatches = (c: VehicleComplexity, filter: PlannerComplexityFilter): boolean => {
  if (filter === "All") return true;
  if (filter === "Easy") return c === VehicleComplexity.Easy;
  if (filter === "Medium") return c === VehicleComplexity.Medium;
  if (filter === "Hard") return c === VehicleComplexity.Hard;
  return true;
};

const ageScore = (v: IPlannerVehicleRow): number => {
  const iso = v.arrivalDate ?? v.collectedOn;
  if (!iso) return 0;
  return new Date(iso).getTime();
};

export default function AwaitingDismantlingPanel({
  vehicles,
  isLoading,
  searchTerm,
  complexityFilter,
  sort,
  colourBy,
  onDropFromBay,
}: AwaitingDismantlingPanelProps) {
  const { t } = useTranslation("production");
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DragTypes.PLANNER_VEHICLE,
      drop: (item: IPlannerDragItem) => {
        if (item.currentBayId) {
          onDropFromBay(item);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [onDropFromBay]
  );

  const isSearching = searchTerm.trim().length > 0;

  const filtered = useMemo(() => {
    const filteredList = vehicles.filter((v) => {
      if (!complexityMatches(v.complexity, complexityFilter)) return false;
      return plannerSearchMatches(v, searchTerm);
    });

    return filteredList.sort((a, b) => {
      if (sort === "Easiest") return a.complexity - b.complexity;
      if (sort === "Hardest") return b.complexity - a.complexity;

      const aScore = ageScore(a);
      const bScore = ageScore(b);
      return sort === "Oldest" ? aScore - bScore : bScore - aScore;
    });
  }, [vehicles, complexityFilter, searchTerm, sort]);

  return (
    <aside
      ref={drop}
      className={classNames(
        "bg-white border border-gray-200 rounded-md flex flex-col h-full overflow-hidden",
        isOver ? "ring-2 ring-primary-400" : ""
      )}
    >
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">
          {t("Awaiting Dismantling")}
        </span>
        <span className="text-xs text-gray-500">
          {isSearching ? `(${filtered.length}/${vehicles.length})` : `(${filtered.length})`}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isLoading ? (
          <LoadingPulse />
        ) : filtered.length === 0 ? (
          <div className="text-center text-xs text-gray-400 mt-6 px-4">
            {isSearching
              ? t("Nothing awaiting dismantling matches this search.")
              : t("No vehicles awaiting dismantling.")}
          </div>
        ) : (
          filtered.map((v) => (
            <PlannerVehicleCard key={v.id} vehicle={v} colourBy={colourBy} />
          ))
        )}
      </div>
    </aside>
  );
}
